import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CalculatorForm from "../components/CalculatorForm";

import NewReportActions from "../components/NewReportActions";
import {
  calculateEmissions,
  fetchCalculationByEmissionId,
} from "../services/emissionsApi";

const HomePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [departure, setDeparture] = useState("");
  const [arrival, setArrival] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [cabinClass, setCabinClass] = useState("business");
  const [tripType, setTripType] = useState("One Way");
  const [operatingCarrierCode, setOperatingCarrierCode] = useState("");
  const [flightNumber, setFlightNumber] = useState("");
  const [departureDate, setDepartureDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  // const [loading, setLoading] = useState(false);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [isValidEmissionId, setIsValidEmissionId] = useState(false);
  const [finalEmissionId, setFinalEmissionId] = useState(null);
  const [flyingHours, setFlyingHours] = useState("");
  const [calculationMode, setCalculationMode] = useState("airport");
  const [isTermsAccepted, setTermsAccepted] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const emissionId = queryParams.get("emissionId");

  useEffect(() => {
    const fetchData = async () => {
      if (emissionId) {
        try {
          const data = await fetchCalculationByEmissionId(emissionId);
          setIsValidEmissionId(true);
          setFinalEmissionId(emissionId);
          setDeparture(data.request.origin || "");
          setArrival(data.request.destination || "");
          setPassengers(data.request.passengers || 1);
          setCabinClass(data.request.cabinClass || "all");
          setTripType(data.request.tripType || "One Way");
          setEmail(data.email || "");
          setTermsAccepted(data.isTermsAccepted || false);
          setOperatingCarrierCode(
            data.flightDetails?.operatingCarrierCode || ""
          );
          setFlightNumber(data.flightDetails?.flightNumber || "");
          setDepartureDate(
            data.request.departureDate || new Date().toISOString().split("T")[0]
          );
          setFlyingHours(data.request.flyingHours || "");

          const status = data.status;
          if (status === "Calculated") {
            const nextStep = data.paymentId?.status === "Paid" ? 4 : 3;
            setCurrentStep(nextStep);
            navigate("/result", { state: { data } });
          }
          if (status === "Pending" || status === "Failed") {
            setError(data.response?.error || "Calculation failed.");
          }
        } catch (err) {
          setError("Invalid or Expired EmissionId. Please try again.");
        } finally {
        }
      }
    };

    fetchData();
  }, [emissionId, navigate]);

  const handleCalculate = async () => {
    if (!passengers || !cabinClass || !tripType || !email || !isTermsAccepted) {
      setError("All fields are required, including email.");
      return null;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      return null;
    }

    if (calculationMode === "airport") {
      if (!departure || !arrival) {
        setError("Departure and Arrival airports are required.");
        return null;
      }
      if (departure === arrival) {
        setError("Departure and Arrival airports cannot be the same.");
        return null;
      }
    } else if (calculationMode === "hours") {
      const n = Number(flyingHours);
      if (!flyingHours || Number.isNaN(n) || n <= 0) {
        setError("Please enter a valid flying hours value (e.g., 2.5).");
        return null;
      }
    }

    setApiLoaded(true);
    setError(null);

    try {
      // If hours-mode, delegate calculation to the backend and navigate
      // to /result/hours with the response shaped similarly to the
      // existing hours flow.
      if (calculationMode === "hours") {
        const payload = {
          mode: "hours",
          flyingHours: Number(flyingHours),
          passengers,
          cabinClass,
          tripType,
          email,
          isTermsAccepted,
        };

        const result = await calculateEmissions(payload);
        if (result && result.success) {
          const stateData = {
            request: payload,
            flightDetails: {},
            googleApiResponse: {
              emissionPerPassenger: result.data?.emissionPerPassenger,
              totalEmission: result.data?.totalEmission,
              modelVersion: result.data?.modelVersion || null,
            },
            paymentId: null,
            emissionId: result.data?.emissionId || null,
          };

          setFinalEmissionId(result.data?.emissionId);
          setIsPaid(result.data?.paymentId?.status === "Paid");
          navigate("/result/hours", {
            state: { data: stateData },
          });
          return { ...stateData, __alreadyNavigated: true };
        }

        setError(result?.error || "Failed to calculate emissions.");
        return null;
      }

      // Airport mode (existing behavior)
      const payload = {
        departure,
        arrival,
        passengers,
        cabinClass,
        tripType,
        operatingCarrierCode,
        flightNumber,
        departureDate,
        email,
        emissionId: isValidEmissionId ? emissionId : finalEmissionId,
        isTermsAccepted,
      };

      const result = await calculateEmissions(payload);

      if (result.success) {
        setFinalEmissionId(result.data?.emissionId);
        setIsPaid(result.data?.paymentId?.status === "Paid");
        navigate("/result", { state: { data: result.data } }); // ✅ Navigate to result page
        return { ...result.data, __alreadyNavigated: true };
      } else {
        setError(result.error || "Failed to calculate emissions.");
        return null;
      }
    } catch (err) {
      setError("Failed to calculate emissions. Please try again.");
      return null;
    } finally {
      setApiLoaded(false);
    }
  };

  return (
    <div className="container mt-5">
      {
        <CalculatorForm
          isDisabled={isPaid}
          departure={departure}
          setDeparture={setDeparture}
          arrival={arrival}
          setArrival={setArrival}
          flyingHours={flyingHours}
          setFlyingHours={setFlyingHours}
          passengers={passengers}
          setPassengers={setPassengers}
          cabinClass={cabinClass}
          setCabinClass={setCabinClass}
          tripType={tripType}
          setTripType={setTripType}
          operatingCarrierCode={operatingCarrierCode}
          setOperatingCarrierCode={setOperatingCarrierCode}
          flightNumber={flightNumber}
          setFlightNumber={setFlightNumber}
          departureDate={departureDate}
          setDepartureDate={setDepartureDate}
          email={email}
          setEmail={setEmail}
          handleCalculate={handleCalculate}
          setEmissions={() => {}}
          setError={() => setError(null)}
          isTermsAccepted={isTermsAccepted}
          setTermsAccepted={setTermsAccepted}
          calculationMode={calculationMode}
          setCalculationMode={setCalculationMode}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      }
      {apiLoaded}
      {error && <div className="alert alert-danger mt-3">{error}</div>}
      <NewReportActions isPaid={isPaid} />
    </div>
  );
};

export default HomePage;
